import { useEffect, useRef, useState } from 'react';

export default function Schedule() {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [openTicketIndexes, setOpenTicketIndexes] = useState<Set<number>>(new Set());
  const [openAdditionalTicketIndexes, setOpenAdditionalTicketIndexes] = useState<Set<number>>(new Set());
  const [openMainTicketIndexes, setOpenMainTicketIndexes] = useState<Set<number>>(new Set());

  const toggleMainTicket = (i: number) => {
    setOpenMainTicketIndexes(prev => {
      const next = new Set(prev);
      if (next.has(i)) {
        next.delete(i);
      } else {
        next.add(i);
      }
      return next;
    });
  };

  const toggleTicket = (i: number) => {
    setOpenTicketIndexes(prev => {
      const next = new Set(prev);
      if (next.has(i)) {
        next.delete(i);
      } else {
        next.add(i);
      }
      return next;
    });
  };

  const toggleAdditionalTicket = (i: number) => {
    setOpenAdditionalTicketIndexes(prev => {
      const next = new Set(prev);
      if (next.has(i)) {
        next.delete(i);
      } else {
        next.add(i);
      }
      return next;
    });
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    const currentRef = sectionRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  const mainTicketSchedules = [
    {
      title: '一般販売（先着）',
      isNew: false,
      content: `販売時間：3月7日(土)10:00～\nチケット料金：\n■指定席：10,000円（税込）\n■自由席：9,000円（税込）\n※未就学児入場不可\n※お一人様１公演につき4枚まで\n※先着での受付です。予定枚数に達し次第受付を終了いたします。`
    },
    {
      title: '当日券（大阪公演）',
      isNew: true,
      content: `販売時間：2026/4/17(金)16:00〜\nチケット料金：\n■指定席：10,500円（税込）\n■自由席：9,500円（税込）\n※未就学児入場不可\n※お一人様１公演につき4枚まで\n※先着での受付です。予定枚数に達し次第受付を終了いたします。`
    }
  ];

  const ticketSchedules = [
    {
      title: '一般販売(先着)',
      items: [
        { label: '受付期間', value: '3月7日(土)10:00～' }
      ],
      itemsMobile: [
        { label: '受付期間', value: '3月7日(土)10:00～' }
      ]
    }
  ];

  const additionalTicketSchedules = [
    {
      title: '一般発売(先着)',
      items: [
        { label: '受付期間', value: '4/4(土)10:00～' }
      ],
      itemsMobile: [
        { label: '受付期間', value: '4/4(土)10:00～' }
      ]
    }
  ];

  const shows = [
    {
      city: '東京公演',
      date: '2026年4月13日（月）',
      time: '17:00開場 / 18:00開演',
      venue: 'Kanadevia Hall',
      ticketUrls: [
        { label: 'ローソンチケット', url: 'https://l-tike.com/irinomiyu/' },
        { label: 'アニメイトチケット', url: 'https://animate-ticket.com/events/79' }
      ]
    },
    {
      city: '大阪公演',
      date: '2026年4月17日（金）',
      time: '17:00開場 / 18:00開演',
      venue: 'Zepp Osaka Bayside',
      ticketUrls: [
        { label: 'ローソンチケット', url: 'https://l-tike.com/irinomiyu/' },
        { label: 'アニメイトチケット', url: 'https://animate-ticket.com/events/79' }
      ]
    },
    {
      city: '福岡公演',
      date: '2026年5月1日（金）',
      time: '17:00開場 / 18:00開演',
      venue: 'Zepp Fukuoka',
      ticketUrls: [
        { label: 'ローソンチケット', url: 'https://l-tike.com/irinomiyu/' },
        { label: 'アニメイトチケット', url: 'https://animate-ticket.com/events/79' }
      ]
    },
    {
      city: '追加公演',
      date: '2026年5月27日（水）',
      time: '17:00開場 / 18:00開演',
      venue: 'Zepp Haneda',
      ticketUrls: [
        { label: 'ローソンチケット', url: 'https://l-tike.com/irinomiyu/' },
        { label: 'アニメイトチケット', url: 'https://animate-ticket.com/events/79' }
      ],
      isNew: true
    }
  ];

  return (
    <section
      ref={sectionRef}
      id="schedule"
      className={`py-20 transition-all duration-1000 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
    >
      <div className="container mx-auto max-w-6xl px-4">
        <div className="text-center mb-16">
          <div className="inline-block bg-[#fcb122] px-8 md:px-12 py-3 md:py-4 shadow-[0_0_40px_#fcb122]">
            <h2 className="text-3xl md:text-5xl text-[#0a0a0a] font-bold">SCHEDULE</h2>
          </div>
        </div>

        {/* チケットスケジュール */}
        <div className="bg-gradient-to-r from-[#1a1a3e] to-[#2a2a4e] border-2 border-[#fcb122]/50 p-6 md:p-8 mb-8">
          <h3 className="text-xl md:text-2xl text-[#fcb122] font-bold mb-6 text-center">
            {/* PC版 */}
            <span className="hidden md:inline">東京・大阪・福岡<br />チケットスケジュール</span>
            {/* スマホ版 */}
            <span className="md:hidden">
              東京・大阪・福岡<br />チケットスケジュール
            </span>
          </h3>

          {/* 折りたたみアコーディオン（PC・スマホ共通） */}
          <div className="space-y-3">
            {mainTicketSchedules.map((s, i) => {
              const open = openMainTicketIndexes.has(i);
              return (
                <div key={i} className="border border-[#fcb122]/40 overflow-hidden">
                  <button
                    onClick={() => toggleMainTicket(i)}
                    className="w-full flex items-center justify-between px-4 py-3 text-left cursor-pointer hover:bg-white/5 transition-colors"
                  >
                    <div className="flex items-center gap-3 pr-2">
                      <span className="text-sm md:text-base text-white font-bold">{s.title}</span>
                      {s.isNew && (
                        <span className="bg-[#fc457e] text-white text-xs px-2 py-0.5 font-bold animate-pulse shadow-[0_0_8px_#fc457e]">
                          NEW
                        </span>
                      )}
                    </div>
                    <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                      <span className={`text-[#fcb122] text-xl transition-transform duration-300 font-dotgothic ${open ? 'rotate-180' : ''}`}>
                        ▼
                      </span>
                    </div>
                  </button>
                  <div
                    className={`transition-all duration-300 overflow-hidden ${open ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}
                  >
                    <div className="px-4 pb-4">
                      <p className="text-xs md:text-sm text-white/90 whitespace-pre-line leading-relaxed">
                        {s.content}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* 追加公演チケットスケジュール */}
        <div className="bg-gradient-to-r from-[#1a1a3e] to-[#2a2a4e] border-2 border-[#fcb122]/50 p-6 md:p-8 mb-8">
          <h3 className="text-xl md:text-2xl text-[#fcb122] font-bold mb-6 text-center">
            {/* PC版 */}
            <span className="hidden md:inline">追加公演（Zepp Haneda）<br />チケットスケジュール</span>
            {/* スマホ版 */}
            <span className="md:hidden">
              追加公演<br />（Zepp Haneda）<br />チケットスケジュール
            </span>
          </h3>

          {/* PC版: 従来の表示 */}
          <div className="hidden md:block space-y-6">
            {additionalTicketSchedules.map((s, i) => (
              <div key={i}>
                <p className="text-base md:text-lg text-white font-bold mb-2">{s.title}</p>
                {s.items.map((item, j) => (
                  <p key={j} className="text-sm md:text-base text-white/90">
                    <span className="text-[#fcb122] font-bold">【{item.label}】</span>
                    <span>{item.value}</span>
                  </p>
                ))}
              </div>
            ))}
          </div>

          {/* スマホ版: アコーディオン */}
          <div className="md:hidden space-y-3">
            {additionalTicketSchedules.map((s, i) => {
              const open = openAdditionalTicketIndexes.has(i);
              return (
                <div key={i} className="border border-[#fcb122]/40 overflow-hidden">
                  <button
                    onClick={() => toggleAdditionalTicket(i)}
                    className="w-full flex items-center justify-between px-4 py-3 text-left cursor-pointer hover:bg-white/5 transition-colors"
                  >
                    <span className="text-sm text-white font-bold pr-2">{s.title}</span>
                    <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
                      <span className={`text-[#fcb122] text-2xl transition-transform duration-300 font-dotgothic ${open ? 'rotate-180' : ''}`}>
                        ▼
                      </span>
                    </div>
                  </button>
                  <div
                    className={`transition-all duration-300 overflow-hidden ${open ? 'max-h-60 opacity-100' : 'max-h-0 opacity-0'}`}
                  >
                    <div className="px-4 pb-4 space-y-3">
                      {s.itemsMobile.map((item, j) => (
                        <p key={j} className="text-xs text-white/90">
                          <span className="text-[#fcb122] font-bold block">【{item.label}】</span>
                          <span className="whitespace-pre-line">{item.value}</span>
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* 公演一覧 */}
        <div className="grid gap-6 md:gap-8">
          {shows.map((show, index) => (
            <div
              key={index}
              className="block bg-gradient-to-r from-[#1a1a3e] to-[#2a2a4e] border-4 border-[#44c4ff] p-6 md:p-8 transition-all duration-300 hover:border-[#1ef99d] hover:shadow-[0_0_40px_#44c4ff] group"
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-lg md:text-2xl text-[#44c4ff] group-hover:text-[#1ef99d] transition-colors font-dotgothic">
                      ▷
                    </span>
                    <h3 className="text-base md:text-xl font-bold text-[#44c4ff] group-hover:text-white transition-colors break-words">
                      {show.city}
                    </h3>
                    {show.isNew && (
                      <span className="bg-[#fc457e] text-white text-xs px-3 py-1 font-bold animate-pulse shadow-[0_0_10px_#fc457e]">
                        NEW
                      </span>
                    )}
                  </div>

                  <div className="space-y-2 text-xs md:text-sm text-white/90">
                    <p className="flex items-center gap-2 break-words">
                      <i className="ri-calendar-line w-4 h-4 flex items-center justify-center text-[#44c4ff] shrink-0"></i>
                      <span className="break-words">{show.date}</span>
                    </p>
                    <p className="flex items-center gap-2 break-words">
                      <i className="ri-time-line w-4 h-4 flex items-center justify-center text-[#44c4ff] shrink-0"></i>
                      <span className="break-words">{show.time}</span>
                    </p>
                    <p className="flex items-center gap-2 break-words">
                      <i className="ri-map-pin-line w-4 h-4 flex items-center justify-center text-[#44c4ff] shrink-0"></i>
                      <span className="break-words">{show.venue}</span>
                    </p>
                  </div>
                </div>

                <div className="flex flex-col items-center justify-center md:justify-end gap-3 shrink-0 md:w-48">
                  {show.ticketUrls.map((ticket, tIdx) => (
                    <a
                      key={tIdx}
                      href={ticket.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full bg-[#44c4ff] text-[#0a0a0a] px-4 py-3 border-2 border-[#44c4ff] text-xs md:text-sm hover:bg-[#1ef99d] hover:border-[#1ef99d] hover:text-[#0a0a0a] transition-all whitespace-nowrap font-bold shadow-[0_0_15px_#44c4ff] hover:shadow-[0_0_20px_#1ef99d] font-press-start cursor-pointer text-center"
                    >
                      {ticket.label} <span className="font-dotgothic">▷</span>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <div className="inline-block bg-[#1a1a3e]/80 border-2 border-[#44c4ff]/50 px-6 py-4 max-w-full">
            <p className="text-xs md:text-sm text-white/70 leading-relaxed break-words">
              ※各公演の詳細・チケット購入は各リンクよりご確認ください。
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
